import React, { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ListBox } from 'primereact/listbox';
import DateInput from './DateInput';
import { ACTIVITY_TYPES, activityLabel, newDetail, normalizeDetail, nextStartTime, sortDetails, uOdmor } from '../../utils/activities';
import { fmtDuration, fmtTime, DAY_MS } from '../../utils/dates';
import { useAlerts } from '../../custom/Alerts';
import { tableEmpty } from './PageHeader';
import AktivnostSimbol from './AktivnostSimbol';
import { IzvorOznaka, zakljucan } from './IzvorAktivnosti';

/* Inline-editabilna tabela detalja aktivnosti jednog dana (Tip / Početak / Kraj / Trajanje).
   Isto ponašanje kao stari DataGrid: "+" dodaje red na vrhu, olovka menja, X briše,
   posle svake izmene lista se normalizuje i roditelj dobija `onChange(list)`.
   `visestruko`: kvadratići za izbor redova + "Obriši aktivnosti" — izabrani redovi postaju odmor (uOdmor).
   `prikaziIzvor`: kolona Izvor (kartica / listić / ručno); red sa kartice (osim nepoznatog) je zaključan — bez izmene i brisanja,
   a izmena drugog reda ne sme da ga preklopi (isto proverava baza). Izmenjen / nov red postaje "ručno". */
export default function ActivityDetailsGrid({
    details, day, onChange, confirmDelete = false, readOnly = false, selectable = false, visestruko = false,
    emptyMessage = 'Nijedna aktivnost nije pronađena', startWidth, scrollHeight = 'flex', extraColumns = [], simboli = true, prednostIzmeni = false,
    prikaziIzvor = false, izaberi = null,
}) {
    const alerts = useAlerts();
    const [edit, setEdit] = useState(null); // { key, isNew, row }
    const [tipRed, setTipRed] = useState(null); // red kome je dvoklikom otvoren izbor tipa
    const [izabrani, setIzabrani] = useState([]);
    const tipPanel = useRef(null);
    const dan = day ? new Date(day).getTime() : null;
    useEffect(() => { setIzabrani([]); }, [dan]); // drugi dan = izbor ispočetka
    /* izbor spolja (Aktivnosti: klik na Traku 1 / Ručno iznad tabele): svi redovi tog izvora koji se smeju brisati */
    useEffect(() => {
        if (!izaberi) return;
        const kljuc = (d) => `${d.izvor || 'RUCNO'}|${d.listicId || ''}`;
        setIzabrani(details.map((d, i) => (d._key ? d : { ...d, _key: `e${d.id}-${i}` })).filter((d) => kljuc(d) === izaberi.kljuc && !zakljucan(d)));
    }, [izaberi]); // eslint-disable-line react-hooks/exhaustive-deps

    const keyOf = (d) => d._key || d.id;
    const withKeys = details.map((d, i) => (d._key ? d : { ...d, _key: `e${d.id}-${i}` }));
    const rows = edit && edit.isNew ? [edit.row, ...withKeys] : withKeys;

    const startNew = () => {
        const start = nextStartTime(details, day);
        const end = new Date(start);
        setEdit({ key: '__new', isNew: true, row: { ...newDetail('driving', start, end), _key: '__new' } });
    };

    const commit = () => {
        /* kao stari grid: samo normalizacija; ispravnost dana proverava zbir od 24:00 */
        const row = normalizeDetail(edit.row);
        const base = edit.isNew ? withKeys : withKeys.filter((d) => keyOf(d) !== edit.key);
        const oznaka = prikaziIzvor ? { izvor: 'RUCNO', listicId: null, listic: null } : {};
        const final = edit.isNew ? { ...row, ...oznaka, _key: undefined } : { ...row, ...oznaka };
        const fa0 = new Date(final.startTime).getTime(), fb0 = new Date(final.endTime).getTime();
        const kart = base.find((d) => zakljucan(d) && new Date(d.startTime).getTime() < fb0 && new Date(d.endTime).getTime() > fa0);
        if (kart) {
            alerts.error(`Aktivnost se preklapa sa aktivnošću sa kartice (${activityLabel(kart.activityType)} ${fmtTime(kart.startTime)}–${fmtTime(kart.endTime)}), koja se ne menja.`);
            return;
        }
        /* prednostIzmeni: dodata/izmenjena aktivnost ima prednost — ostali redovi se oko nje skraćuju ili dele
           (Bulk / Taho listići: ranije je nova aktivnost preko postojeće nestajala) */
        const ostali = prednostIzmeni ? base.flatMap((d) => {
            const a = new Date(d.startTime).getTime(), b = new Date(d.endTime).getTime();
            const fa = new Date(final.startTime).getTime(), fb = new Date(final.endTime).getTime();
            if (fb <= fa || b <= fa || a >= fb) return [d];
            const delovi = [];
            if (a < fa) delovi.push(normalizeDetail({ ...d, endTime: new Date(fa) }));
            if (b > fb) delovi.push(normalizeDetail({ ...d, startTime: new Date(fb), _key: a < fa ? `${keyOf(d)}-2` : d._key }));
            return delovi;
        }) : base;
        onChange(sortDetails([...ostali, final]));
        setEdit(null);
    };

    const remove = async (row) => {
        if (zakljucan(row)) return;
        if (confirmDelete && !(await alerts.confirm('Potvrdi', 'Da li ste sigurni da želite da obrišete aktivnost?'))) return;
        onChange(withKeys.filter((d) => keyOf(d) !== keyOf(row)));
    };

    /* više redova odjednom: umesto obrisanih ostaje odmor (dan ostaje pun) */
    const obrisiIzabrane = async () => {
        const n = izabrani.length;
        if (!n) return;
        const tekst = `Obrisati ${n === 1 ? 'izabranu aktivnost' : `${n} izabranih aktivnosti`}? Na njihovom mestu biće odmor.`;
        if (!(await alerts.confirm('Potvrdi', tekst, { severity: 'danger' }))) return;
        onChange(uOdmor(withKeys, new Set(izabrani.filter((z) => !zakljucan(z)).map(keyOf)), keyOf, { oznaci: prikaziIzvor }));
        setIzabrani([]);
    };

    const setField = (field, value) => setEdit((e) => ({ ...e, row: { ...e.row, [field]: value } }));

    /* time-only editor: datumski deo ostaje referentni dan aktivnosti. */
    const timeValue = (field) => {
        const v = edit.row[field];
        return v ? new Date(v) : null;
    };
    const setTime = (field, v) => {
        if (!v) return setField(field, null);
        const base = new Date(day);
        base.setHours(v.getHours(), v.getMinutes(), 0, 0);
        setField(field, base);
    };

    const isEditing = (row) => edit && keyOf(row) === edit.key;

    /* dvoklik na red: lista tipova ispod reda, izbor odmah upisuje (brza ispravka pogrešnog tipa).
       Jedan panel van tabele — meni unutar ćelije DataTable ponovo iscrtava i zatvara. */
    const brziTip = (e) => {
        if (readOnly || edit || zakljucan(e.data)) return;
        const tr = e.originalEvent.target.closest('tr');
        setTipRed(e.data);
        tipPanel.current?.show(e.originalEvent, tr || e.originalEvent.target);
    };
    const upisiTip = (tip) => {
        const row = tipRed;
        tipPanel.current?.hide();
        if (row && tip && tip !== row.activityType) {
            const oznaka = prikaziIzvor ? { izvor: 'RUCNO', listicId: null, listic: null } : {};
            onChange(sortDetails(withKeys.map((d) => (keyOf(d) === keyOf(row) ? normalizeDetail({ ...d, ...oznaka, activityType: tip }) : d))));
        }
    };

    const typeBody = (row) =>
        isEditing(row) ? (
            <Dropdown value={edit.row.activityType} options={ACTIVITY_TYPES} onChange={(e) => setField('activityType', e.value)} style={{ width: '100%' }} />
        ) : simboli ? <AktivnostSimbol tip={row.activityType} label={activityLabel(row.activityType)} /> : activityLabel(row.activityType);

    const timeBody = (field) => (row) =>
        isEditing(row) ? (
            <DateInput value={timeValue(field)} onChange={(e) => setTime(field, e.value)} timeOnly hourFormat="24" placeholder="Unesite datum/vreme" />
        ) : fmtTime(row[field]);

    const totalBody = (row) => {
        const ms = isEditing(row) ? normalizeDetail(edit.row).totalTime : row.totalTime;
        return ms === DAY_MS ? '24:00' : fmtDuration(ms);
    };

    const actionsBody = (row) =>
        isEditing(row) ? (
            <span className="p-buttonset">
                <Button type="button" icon="pi pi-check" rounded text severity="success" onClick={commit} tooltip="Potvrdi" aria-label="Potvrdi izmenu" />
                <Button type="button" icon="pi pi-times" rounded text severity="secondary" onClick={() => setEdit(null)} tooltip="Otkaži" aria-label="Otkaži izmenu" />
            </span>
        ) : zakljucan(row) ? (
            <span className="red-zakljucan" title="Aktivnost sa kartice — ne menja se i ne briše" aria-label="Aktivnost sa kartice, zaključana">
                <i className="pi pi-lock" />
            </span>
        ) : (
            <span>
                <Button type="button" icon="pi pi-pencil" rounded text severity="secondary" onClick={() => setEdit({ key: keyOf(row), isNew: false, row: { ...row } })}
                    disabled={!!edit} aria-label="Izmeni aktivnost" tooltip="Izmeni" />
                <Button type="button" icon="pi pi-trash" rounded text severity="danger" onClick={() => remove(row)} disabled={!!edit} aria-label="Obriši aktivnost" tooltip="Obriši" />
            </span>
        );

    const addHeader = (
        <Button type="button" icon="pi pi-plus" rounded text onClick={startNew} disabled={!!edit} tooltip="Dodaj aktivnost" aria-label="Dodaj aktivnost" />
    );

    /* kao stari grid: Enter potvrđuje, Esc odustaje od izmene reda (osim dok je otvoren padajući meni) */
    const onKeyDown = (e) => {
        if (!edit || (e.key !== 'Enter' && e.key !== 'Escape')) return;
        if (document.querySelector('.p-dropdown-panel')) return;
        e.preventDefault();
        if (e.key === 'Enter') commit(); else setEdit(null);
    };

    const vise = visestruko && !readOnly;
    /* izbor važi samo za redove koji još postoje (posle izmene dana) */
    const izbor = vise ? izabrani.filter((z) => withKeys.some((d) => keyOf(d) === keyOf(z))) : [];
    const zaBrisanje = new Set(izbor.map(keyOf));
    /* pregled izabranog pre brisanja: tip, vreme i trajanje svakog reda (po vremenu), klik na × skida izbor */
    const izborPoVremenu = sortDetails(withKeys.filter((d) => zaBrisanje.has(keyOf(d))));
    const izborUkupno = izborPoVremenu.reduce((s, d) => s + (new Date(d.endTime) - new Date(d.startTime)), 0);
    const trajanje = (ms) => (ms === DAY_MS ? '24:00' : fmtDuration(ms));

    return (
        <div onKeyDown={onKeyDown} className={vise ? 'grid-sa-alatima' : undefined} style={{ height: '100%' }}>
        {vise && (
            <div className="grid-alati">
                <div className="grid-alati-red">
                    <span className="muted">
                        {izbor.length ? `Za brisanje: ${izbor.length} (${trajanje(izborUkupno)} h) — postaje odmor` : 'Označite redove za brisanje'}
                    </span>
                    <span className="grid-alati-akcije">
                        {izbor.length > 0 && (
                            <Button type="button" label="Poništi izbor" icon="pi pi-times" severity="secondary" text size="small" onClick={() => setIzabrani([])} />
                        )}
                        <span className="tt-omotac">
                            <Button type="button" label="Obriši aktivnosti" icon="pi pi-trash" severity="danger" outlined size="small"
                                onClick={obrisiIzabrane} disabled={!izbor.length || !!edit}
                                tooltip="Izabrane aktivnosti se brišu, na njihovom mestu ostaje odmor" tooltipOptions={{ position: 'top', showOnDisabled: true }} />
                        </span>
                    </span>
                </div>
                {izbor.length > 0 && (
                    <ul className="za-brisanje" aria-label="Aktivnosti izabrane za brisanje">
                        {izborPoVremenu.map((d) => (
                            <li key={keyOf(d)}>
                                <AktivnostSimbol tip={d.activityType} label={activityLabel(d.activityType)} />
                                <span className="num">{fmtTime(d.startTime)}–{fmtTime(d.endTime)}</span>
                                <span className="muted num">{trajanje(new Date(d.endTime) - new Date(d.startTime))}</span>
                                <button type="button" className="za-brisanje-x" onClick={() => setIzabrani(izbor.filter((z) => keyOf(z) !== keyOf(d)))}
                                    aria-label={`Skini iz izbora ${activityLabel(d.activityType)} ${fmtTime(d.startTime)}`} title="Skini iz izbora">
                                    <i className="pi pi-times" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        )}
        {/* cellMemo={false}: ćelije zavise od stanja izmene (edit), a ne samo od podataka reda — sa memoizacijom se red ne otvara za izmenu */}
        <DataTable value={rows} dataKey="_key" size="small" cellMemo={false} emptyMessage={tableEmpty(emptyMessage, 'pi pi-clock')} scrollable scrollHeight={scrollHeight}
            rowClassName={(r) => (zaBrisanje.has(keyOf(r)) ? 'row-za-brisanje'
                : isEditing(r) || (tipRed && keyOf(r) === keyOf(tipRed)) ? 'row-selected' : '')} onRowDoubleClick={brziTip}
            {...(vise ? { selectionMode: 'checkbox', selection: izbor, onSelectionChange: (e) => setIzabrani(e.value), isDataSelectable: (e) => !edit && keyOf(e.data) !== '__new' && !zakljucan(e.data) } : {})}>
            {vise && <Column selectionMode="multiple" headerStyle={{ width: '2.75rem' }} />}
            {!readOnly && <Column header={addHeader} body={actionsBody} headerClassName="actions" bodyClassName="actions" style={{ width: '6.5rem' }} />}
            <Column header="Tip" body={typeBody} style={{ minWidth: '9rem' }} />
            <Column header="Vreme početka" body={timeBody('startTime')} style={{ minWidth: startWidth || '9rem' }} />
            <Column header="Vreme završetka" body={timeBody('endTime')} style={{ minWidth: startWidth || '9rem' }} />
            <Column header="Vreme trajanja" body={totalBody} headerClassName="num" bodyClassName="num" style={{ minWidth: '7rem' }} />
            {prikaziIzvor && <Column header="Izvor" body={(r) => (isEditing(r) ? <IzvorOznaka d={{ izvor: 'RUCNO' }} /> : <IzvorOznaka d={r} />)} style={{ minWidth: '8rem', maxWidth: '16rem' }} />}
            {/* dodatne kolone samo za prikaz (npr. izvor reda u bulk unosu listića) */}
            {extraColumns.map((c) => <Column key={c.header} header={c.header} body={c.body} style={c.style} />)}
        </DataTable>
        <OverlayPanel ref={tipPanel} onHide={() => setTipRed(null)} className="tip-panel">
            <ListBox value={tipRed?.activityType} options={ACTIVITY_TYPES} onChange={(e) => upisiTip(e.value)} aria-label="Tip aktivnosti"
                itemTemplate={simboli ? (o) => <AktivnostSimbol tip={o.value} label={o.label} /> : undefined} />
        </OverlayPanel>
        </div>
    );
}

